import { useEffect, useReducer, useRef, useState } from 'react';
import { CameraImageTimelineResponse } from '../types';

// Action types for the image preloader reducer
type ImageAction =
  | { type: 'RESET' }
  | {
      type: 'ADD_IMAGE';
      uri: string;
      base64: string;
      imageElement: HTMLImageElement;
    }
  | { type: 'ADD_URI'; uri: string; displayUri: string; imageElement: HTMLImageElement } // Fallback for CORS issues
  | { type: 'LOADING_ERROR'; uri: string };

// State for managing preloaded images
interface PreloadedImageState {
  htmlElements: Record<string, HTMLImageElement>;
  base64Images: Record<string, string>;
}

// Reducer for managing preloaded images to avoid race conditions
const imageReducer = (state: PreloadedImageState, action: ImageAction): PreloadedImageState => {
  switch (action.type) {
    case 'RESET':
      return { htmlElements: {}, base64Images: {} };
    case 'ADD_IMAGE':
      return {
        htmlElements: {
          ...state.htmlElements,
          [action.uri]: action.imageElement,
        },
        base64Images: {
          ...state.base64Images,
          [action.uri]: action.base64,
        },
      };
    case 'ADD_URI':
      return {
        htmlElements: {
          ...state.htmlElements,
          [action.uri]: action.imageElement,
        },
        base64Images: {
          ...state.base64Images,
          [action.uri]: action.displayUri, // Store display URI as fallback
        },
      };
    case 'LOADING_ERROR':
      // Keep current state, don't add failed image
      return state;
    default:
      return state;
  }
};

// Helper function to convert image to base64
const imageToDataURL = (img: HTMLImageElement): string => {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx?.drawImage(img, 0, 0);
  return canvas.toDataURL('image/jpeg');
};

const appendAuthToken = (imageUri: string, authToken?: string): string => {
  if (!authToken) return imageUri;
  try {
    const url = new URL(imageUri);
    if (url.searchParams.has('token')) return imageUri;
  } catch {
    if (/[?&]token=/.test(imageUri)) return imageUri;
  }
  const separator = imageUri.includes('?') ? '&' : '?';
  return `${imageUri}${separator}token=${encodeURIComponent(authToken)}`;
};

interface UseImagePreloaderOptions {
  cameraTimeline?: CameraImageTimelineResponse | null;
  authToken?: string;
  pogImageUri?: string; // Additional POG image to preload
  priorityImageId?: number | null;
}

interface UseImagePreloaderReturn {
  preloadedImages: Record<string, HTMLImageElement>;
  preloadedImagesB64: Record<string, string>;
  isLoadingFirstImage: boolean;
  isLoading: boolean;
}

export const useImagePreloader = ({
  cameraTimeline,
  authToken,
  pogImageUri,
  priorityImageId,
}: UseImagePreloaderOptions): UseImagePreloaderReturn => {
  const [imageState, dispatchImages] = useReducer(imageReducer, {
    htmlElements: {},
    base64Images: {},
  });
  const [isLoadingFirstImage, setIsLoadingFirstImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const cameraTimelineRef = useRef(cameraTimeline);
  const loadedUrisRef = useRef<Set<string>>(new Set());
  const activeLoadKeyRef = useRef('');

  cameraTimelineRef.current = cameraTimeline;

  const timelineImages = cameraTimeline?.images ?? [];
  const imageCollectionKey =
    timelineImages.length === 0
      ? ''
      : [
          timelineImages.map((image) => `${image.imageId}:${image.imageUri}`).join('|'),
          Object.values(cameraTimeline?.pogs ?? {})
            .map((pog) => pog?.pogImageUri)
            .filter((imageUri): imageUri is string => Boolean(imageUri))
            .sort()
            .join('|'),
          pogImageUri ?? '',
        ].join('::');

  // Preload the active image first so viewers can render immediately. Conversion to
  // base64 is best-effort; signed CDN URLs are still valid display sources.
  useEffect(() => {
    const currentTimeline = cameraTimelineRef.current;
    if (!currentTimeline?.images || currentTimeline.images.length === 0) {
      dispatchImages({ type: 'RESET' });
      loadedUrisRef.current = new Set();
      activeLoadKeyRef.current = '';
      setIsLoadingFirstImage(false);
      setIsLoading(false);
      return;
    }

    const activeLoadKey = `${authToken ?? ''}::${imageCollectionKey}`;
    if (activeLoadKeyRef.current !== activeLoadKey) {
      dispatchImages({ type: 'RESET' });
      loadedUrisRef.current = new Set();
      activeLoadKeyRef.current = activeLoadKey;
    }

    let loadedCount = 0;
    let totalCount = 0;
    let cancelled = false;
    setIsLoadingFirstImage(true);
    setIsLoading(true);

    const checkFirstImageLoaded = () => {
      if (!cancelled && loadedCount === 1) {
        setIsLoadingFirstImage(false);
      }
    };

    const checkAllImagesLoaded = () => {
      if (!cancelled && loadedCount >= totalCount) {
        setIsLoading(false);
      }
    };

    // Collect all images to preload (timeline images + POG images)
    const allImages = [...currentTimeline.images];

    // Collect POG images
    const pogImages: typeof allImages = [];

    // Add POG images from timeline
    if (currentTimeline.pogs) {
      Object.values(currentTimeline.pogs).forEach((pog) => {
        if (pog && !allImages.find((img) => img.imageUri === pog.pogImageUri)) {
          pogImages.push({
            imageId: 0,
            imageUri: pog.pogImageUri,
            detectedAt: '',
            templateId: 0,
            ilSectionIds: [],
          });
        }
      });
    }

    // Add additional POG image if provided
    if (pogImageUri && !pogImages.find((img) => img.imageUri === pogImageUri)) {
      pogImages.push({
        imageId: 0,
        imageUri: pogImageUri,
        detectedAt: '',
        templateId: 0,
        ilSectionIds: [],
      });
    }

    // Priority images: selected timeline image + POG images
    const firstImage =
      allImages.find((image) => image.imageId === priorityImageId) ||
      (allImages.length > 0 ? allImages[0] : null);
    const priorityImages = firstImage ? [firstImage, ...pogImages] : pogImages;

    // Remaining images (excluding the selected priority one)
    const remainingImages = (
      firstImage ? allImages.filter((image) => image.imageUri !== firstImage.imageUri) : allImages
    ).sort((left, right) => {
      if (!firstImage) return 0;
      return (
        Math.abs(left.imageId - firstImage.imageId) - Math.abs(right.imageId - firstImage.imageId)
      );
    });
    totalCount = Math.max(1, priorityImages.length);

    // Helper function to load a single image
    const loadImageElement = (src: string, crossOrigin?: 'anonymous') =>
      new Promise<HTMLImageElement | null>((resolve) => {
        const img = new Image();
        if (crossOrigin) {
          img.crossOrigin = crossOrigin;
        }
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });

    const handleLoadedImage = (
      imageItem: (typeof allImages)[0],
      img: HTMLImageElement | null,
      displayUri: string
    ) => {
      loadedCount++;
      if (!img) {
        dispatchImages({
          type: 'LOADING_ERROR',
          uri: imageItem.imageUri,
        });
      } else {
        try {
          const base64 = imageToDataURL(img);
          dispatchImages({
            type: 'ADD_IMAGE',
            uri: imageItem.imageUri,
            base64,
            imageElement: img,
          });
        } catch (error) {
          console.debug('Using image URI fallback after base64 conversion failed:', error);
          dispatchImages({
            type: 'ADD_URI',
            uri: imageItem.imageUri,
            displayUri,
            imageElement: img,
          });
        }
      }
      checkFirstImageLoaded();
      checkAllImagesLoaded();
    };

    const loadImage = async (imageItem: (typeof allImages)[0]) => {
      if (loadedUrisRef.current.has(imageItem.imageUri)) {
        loadedCount++;
        checkFirstImageLoaded();
        checkAllImagesLoaded();
        return;
      }
      const authenticatedUri = appendAuthToken(imageItem.imageUri, authToken);
      let displayUri = authenticatedUri;
      let image = await loadImageElement(authenticatedUri, 'anonymous');
      if (!image) {
        image = await loadImageElement(authenticatedUri);
      }
      if (!image && authenticatedUri !== imageItem.imageUri) {
        displayUri = imageItem.imageUri;
        image = await loadImageElement(imageItem.imageUri);
      }
      if (cancelled) return;
      if (image) {
        displayUri = image.src || displayUri;
        loadedUrisRef.current.add(imageItem.imageUri);
      }
      handleLoadedImage(imageItem, image, displayUri);
    };

    (async () => {
      await Promise.all(priorityImages.map((imageItem) => loadImage(imageItem)));
      if (!cancelled) {
        setIsLoading(false);
        setIsLoadingFirstImage(false);
      }

      if (!cancelled && remainingImages.length > 0) {
        window.setTimeout(() => {
          if (cancelled) return;
          remainingImages.forEach((imageItem, index) => {
            window.setTimeout(() => {
              if (!cancelled) void loadImage(imageItem);
            }, index * 75);
          });
        }, 150);
      }
    })();

    return () => {
      // Cancel any pending operations
      cancelled = true;
    };
  }, [imageCollectionKey, authToken, pogImageUri, priorityImageId]);

  return {
    preloadedImages: imageState.htmlElements,
    preloadedImagesB64: imageState.base64Images,
    isLoadingFirstImage,
    isLoading,
  };
};
